import { useParams, useNavigate } from 'react-router-dom'
import { useAppPath } from '../../hooks/useAppPath'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowLeft,
  Building2,
  MapPin,
  TrendingUp,
  Clock,
  DollarSign,
  BarChart3,
  Calendar,
  CheckCircle,
  AlertCircle,
  FileText,
  AlertTriangle,
  Heart,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card'
import { Button } from '../ui/Button'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/Tabs'
import { marketplaceApi } from '../../services/api/marketplace'
import { DstListing } from '../../services/api/admin'
import { cn } from '../../lib/utils'
import { assetClassBadgeClass } from '../../lib/marketplaceFormatters'
import { ImageCarousel } from './ImageCarousel'
import { SponsorMonogram } from './SponsorMonogram'
import { ComplianceFooter } from './ComplianceFooter'
import { useAuth } from '../../contexts/AuthContext'
import { useMarketplaceFavorites } from '../../hooks/useMarketplaceFavorites'

function formatCents(cents: number | null): string {
  if (cents == null) return '—'
  const dollars = cents / 100
  if (dollars >= 1_000_000) return `$${(dollars / 1_000_000).toFixed(1)}M`
  if (dollars >= 1_000) return `$${(dollars / 1_000).toFixed(0)}K`
  return `$${dollars.toLocaleString('en-US')}`
}

function formatHoldPeriod(months: number | null): string {
  if (months == null) return '—'
  const years = months / 12
  return `${years % 1 === 0 ? years.toFixed(0) : years.toFixed(1)} yr${years !== 1 ? 's' : ''}`
}

function formatReturnRange(min: number | null, max: number | null): string {
  if (min == null && max == null) return '—'
  if (min == null) return `up to ${max}%`
  if (max == null) return `${min}%+`
  if (min === max) return `${min}%`
  return `${min}–${max}%`
}

function formatDistributionFrequency(freq: DstListing['dstDistributionFrequency']): string {
  if (freq == null) return '—'
  return freq.charAt(0).toUpperCase() + freq.slice(1)
}

interface RailStatProps {
  icon: React.ReactNode
  label: string
  value: string
  highlight?: boolean
}

function RailStat({ icon, label, value, highlight }: RailStatProps) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-border last:border-0">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        {icon}
        {label}
      </div>
      <span
        className={cn(
          'text-sm font-semibold',
          highlight ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground'
        )}
      >
        {value}
      </span>
    </div>
  )
}

const DST_RISKS = [
  'DST interests are illiquid and cannot be easily sold or redeemed prior to the end of the holding period.',
  'Leverage risk: many DSTs use financing, which can amplify losses and result in loss of principal.',
  'Master-tenant default risk: the DST sponsor acts as master tenant; default could impact distributions.',
  'Sponsor concentration risk: performance depends on the capabilities of a single sponsor.',
  'IRS recharacterization risk: if the exchange is improperly structured, it may not qualify under IRC §1031.',
]

interface ListingDetailViewProps {
  listing: DstListing
}

export function ListingDetailView({ listing }: ListingDetailViewProps) {
  const { user } = useAuth()
  const { isFavorite, toggle } = useMarketplaceFavorites()
  const assetClass = listing.dstAssetClass ?? 'other'
  const fullLocation = [listing.addressCity, listing.addressState].filter(Boolean).join(', ') || '—'
  const sponsorInitials = (listing.sponsor ?? 'DST').slice(0, 2).toUpperCase()
  const favorited = isFavorite(listing.id)

  function handleRequestPacket() {
    toast.success('Coming soon — your request has been noted!')
  }

  function handleAddToList() {
    toast.success('Coming soon — identification list feature in progress.')
  }

  return (
    <div className="flex flex-col">
      <ImageCarousel
        images={listing.images}
        title={listing.title}
        sponsor={listing.sponsor}
      />

      <div className="flex flex-col lg:flex-row gap-8 p-6 lg:p-8">
        <div className="flex-1 min-w-0">
          <Tabs defaultValue="property">
            <TabsList className="mb-6 overflow-x-auto flex-nowrap">
              <TabsTrigger value="property">Property</TabsTrigger>
              <TabsTrigger value="sponsor">Sponsor</TabsTrigger>
              <TabsTrigger value="returns">Returns</TabsTrigger>
              <TabsTrigger value="risks">Risks</TabsTrigger>
              <TabsTrigger value="documents">Documents</TabsTrigger>
            </TabsList>

            <TabsContent value="property">
              <div className="space-y-4">
                <div>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    {listing.qualifiedStatus === 'qualified' && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-800 dark:text-emerald-300 px-3 py-1 text-sm font-semibold">
                        <CheckCircle className="h-4 w-4" aria-hidden="true" />
                        Qualified DST
                      </span>
                    )}
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-3 py-1 text-sm font-semibold capitalize',
                        assetClassBadgeClass(listing.dstAssetClass)
                      )}
                    >
                      {assetClass}
                    </span>
                  </div>
                  <div className="flex items-start gap-2 mb-1">
                    <h1 className="text-2xl font-bold text-foreground flex-1">{listing.title}</h1>
                    {user?.id && (
                      <button
                        type="button"
                        aria-label={favorited ? `Remove ${listing.title} from favorites` : `Save ${listing.title} to favorites`}
                        onClick={() => toggle(listing.id)}
                        className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full border border-border bg-background shadow-sm transition-colors hover:bg-muted mt-0.5"
                      >
                        <Heart
                          className={cn(
                            'h-4 w-4',
                            favorited ? 'fill-rose-500 text-rose-500' : 'text-muted-foreground'
                          )}
                          aria-hidden="true"
                        />
                      </button>
                    )}
                  </div>
                  {listing.sponsor && (
                    <p className="text-muted-foreground font-medium">{listing.sponsor}</p>
                  )}
                  <div className="flex items-center gap-1.5 text-muted-foreground mt-1">
                    <MapPin className="h-4 w-4" aria-hidden="true" />
                    <span className="text-sm">{fullLocation}</span>
                  </div>
                </div>
                {listing.description && (
                  <div className="prose prose-sm max-w-none text-muted-foreground leading-relaxed">
                    {listing.description}
                  </div>
                )}
                <dl className="grid grid-cols-2 gap-4">
                  {listing.dstAssetClass && (
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wide">Asset Class</dt>
                      <dd className="text-sm font-medium text-foreground capitalize mt-0.5">
                        {listing.dstAssetClass}
                      </dd>
                    </div>
                  )}
                  {listing.publishedAt && (
                    <div>
                      <dt className="text-xs text-muted-foreground uppercase tracking-wide">Listed</dt>
                      <dd className="text-sm font-medium text-foreground mt-0.5">
                        {new Date(listing.publishedAt).toLocaleDateString('en-US', {
                          year: 'numeric', month: 'short', day: 'numeric',
                        })}
                      </dd>
                    </div>
                  )}
                </dl>
                {listing.subProperties.length > 0 && (
                  <section className="space-y-3">
                    <h3 className="text-lg font-semibold">Properties in this fund</h3>
                    <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
                      {listing.subProperties.map((subProperty, index) => (
                        <div
                          key={index}
                          className="rounded-lg overflow-hidden border border-border bg-card"
                        >
                          {subProperty.imageUrl && (
                            <img
                              src={subProperty.imageUrl}
                              alt={subProperty.name}
                              className="w-full h-40 object-cover"
                            />
                          )}
                          <div className="p-4 space-y-1">
                            <p className="font-semibold">{subProperty.name}</p>
                            <p className="text-sm text-muted-foreground">{subProperty.address}</p>
                            <p className="text-sm font-mono">${subProperty.value.toLocaleString()}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </div>
            </TabsContent>

            <TabsContent value="sponsor">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div className="h-16 w-16 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                    <SponsorMonogram
                      sponsor={listing.sponsor}
                      textClassName="text-2xl"
                    />
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">
                      {listing.sponsor ? `About ${listing.sponsor}` : 'About this Sponsor'}
                    </h2>
                    {listing.sponsor && (
                      <p className="text-sm text-muted-foreground">{sponsorInitials} · DST Sponsor</p>
                    )}
                  </div>
                </div>
                <Card>
                  <CardContent className="pt-5">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Sponsor profile coming soon — contact your advisor for full due diligence materials,
                      track record, and AUM details.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="returns">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Target Return</p>
                    <p className="text-4xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                      {formatReturnRange(listing.dstExpectedReturnMin, listing.dstExpectedReturnMax)}
                    </p>
                  </div>
                </div>
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-4 w-4" aria-hidden="true" />
                      Financial Details
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-0">
                    <RailStat
                      icon={<DollarSign className="h-4 w-4" aria-hidden="true" />}
                      label="Minimum Investment"
                      value={formatCents(listing.dstMinInvestmentCents)}
                    />
                    <RailStat
                      icon={<Clock className="h-4 w-4" aria-hidden="true" />}
                      label="Hold Period"
                      value={formatHoldPeriod(listing.dstHoldPeriodMonths)}
                    />
                    <RailStat
                      icon={<Calendar className="h-4 w-4" aria-hidden="true" />}
                      label="Distribution Frequency"
                      value={formatDistributionFrequency(listing.dstDistributionFrequency)}
                    />
                    <RailStat
                      icon={<BarChart3 className="h-4 w-4" aria-hidden="true" />}
                      label="Cap Rate"
                      value={listing.capRate != null ? `${listing.capRate}%` : '—'}
                    />
                    <RailStat
                      icon={<DollarSign className="h-4 w-4" aria-hidden="true" />}
                      label="Offering Size"
                      value={formatCents(listing.dstOfferingSizeCents)}
                    />
                    <RailStat
                      icon={<DollarSign className="h-4 w-4" aria-hidden="true" />}
                      label="Asking Price"
                      value={formatCents(listing.askingPriceCents)}
                    />
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="risks">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2 text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                      DST Risk Factors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {DST_RISKS.map((risk) => (
                        <li
                          key={risk}
                          className="flex gap-2 text-sm text-muted-foreground leading-relaxed"
                        >
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-500 flex-shrink-0" aria-hidden="true" />
                          {risk}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="documents">
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <FileText className="h-4 w-4" aria-hidden="true" />
                      Offering Documents
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {[
                        'Private Placement Memorandum (PPM)',
                        'Subscription Agreement',
                        'Sponsor Track Record',
                      ].map((doc) => (
                        <li
                          key={doc}
                          className="flex items-center gap-2 text-sm text-muted-foreground"
                        >
                          <FileText className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                          <span>{doc}</span>
                          <span className="ml-auto text-xs bg-muted px-2 py-0.5 rounded-full">
                            Available on request
                          </span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </div>

        <aside className="w-full lg:w-80 flex-shrink-0">
          <div className="sticky top-24 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <BarChart3 className="h-4 w-4" aria-hidden="true" />
                  Financial Details
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <RailStat
                  icon={<TrendingUp className="h-4 w-4" aria-hidden="true" />}
                  label="Target Return"
                  value={formatReturnRange(listing.dstExpectedReturnMin, listing.dstExpectedReturnMax)}
                  highlight
                />
                <RailStat
                  icon={<DollarSign className="h-4 w-4" aria-hidden="true" />}
                  label="Min Investment"
                  value={formatCents(listing.dstMinInvestmentCents)}
                />
                <RailStat
                  icon={<Clock className="h-4 w-4" aria-hidden="true" />}
                  label="Hold Period"
                  value={formatHoldPeriod(listing.dstHoldPeriodMonths)}
                />
                {listing.sponsor && (
                  <RailStat
                    icon={<Building2 className="h-4 w-4" aria-hidden="true" />}
                    label="Sponsor"
                    value={listing.sponsor}
                  />
                )}
                {listing.dstAssetClass && (
                  <RailStat
                    icon={<BarChart3 className="h-4 w-4" aria-hidden="true" />}
                    label="Asset Class"
                    value={listing.dstAssetClass}
                  />
                )}
                <div className="mt-4 space-y-2">
                  <Button
                    className="w-full"
                    size="lg"
                    onClick={handleRequestPacket}
                  >
                    Request investor packet
                  </Button>
                  <Button
                    className="w-full"
                    size="lg"
                    variant="outline"
                    onClick={handleAddToList}
                  >
                    Add to identification list
                  </Button>
                </div>
                <div className="mt-4 pt-4 border-t border-border">
                  <p className="text-[10px] text-muted-foreground leading-relaxed">
                    Accredited investor status required. Open1031 verifies accreditation before any
                    subscription. All offerings subject to PPM. See Form ADV at{' '}
                    <a href="/adv" className="underline">
                      /adv
                    </a>
                    .
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </aside>
      </div>

      <ComplianceFooter />
    </div>
  )
}

export function ListingDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const appPath = useAppPath()

  const { data: listing, status, error } = useQuery({
    queryKey: ['marketplace', 'listing', id],
    queryFn: () => marketplaceApi.getListing(id!),
    enabled: !!id,
  })

  function handleBack() {
    navigate(appPath('/marketplace'))
  }

  return (
    <div className="max-w-5xl mx-auto">
      <div className="px-4 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="gap-2 mb-4"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Back to Marketplace
        </Button>
      </div>

      {status === 'pending' && (
        <div
          className="space-y-4 p-4"
          aria-label="Loading listing"
        >
          <div className="aspect-video bg-muted animate-pulse rounded-2xl" />
          <div className="h-8 bg-muted animate-pulse rounded-lg w-2/3" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="h-48 bg-muted animate-pulse rounded-[28px]" />
        </div>
      )}

      {status === 'error' && (
        <div className="p-4">
          <Card className="p-12 text-center">
            {(error as any)?.status === 404 ? (
              <>
                <Building2 className="h-16 w-16 text-muted-foreground mx-auto mb-4" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-foreground mb-2">Listing not found</h2>
                <p className="text-muted-foreground mb-4">
                  This listing may have been removed or is no longer available.
                </p>
                <Button onClick={handleBack}>Back to Marketplace</Button>
              </>
            ) : (
              <>
                <AlertCircle className="h-16 w-16 text-destructive mx-auto mb-4" aria-hidden="true" />
                <h2 className="text-lg font-semibold text-foreground mb-2">Unable to load listing</h2>
                <p className="text-muted-foreground mb-4">
                  Something went wrong. Please try again.
                </p>
                <Button onClick={() => window.location.reload()}>Retry</Button>
              </>
            )}
          </Card>
        </div>
      )}

      {status === 'success' && listing && (
        <ListingDetailView listing={listing} />
      )}
    </div>
  )
}
